import * as React from 'react';
import classnames from 'classnames';
import { snakeCase } from 'lodash';

import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';

import { __t, applyTranslations } from 'utils/language';
import { getDisplayDateRange } from 'utils/date';
import {
  CONTENT_APPROVAL_TIMELINE,
  CONTENT_GO_LIVE_TIMELINE,
  CURRENT_TASK,
  DELIVERABLE_TYPE_ZALANDO_OUTFIT,
  INSIGHTS_PROVISION_TIMELINE,
  NEXT_TASK,
  TASK,
  UPCOMING_TASK,
  getDeliverableTimelineStatusType,
  isEntertainmentVideo
} from 'utils/deliverables';

import { Deliverable, DeliverableTimelineType, SubcampaignState } from 'types';

import './deliverable-timeline.scss';

applyTranslations(require('./deliverable-timeline.i18n.json'));

type Props = {
  deliverable: Deliverable,
  isForBrand?: boolean,
  subcampaignState: SubcampaignState
};

const APPROVED = 'approved';
const IN_PROGRESS = 'in_progress';
const NOT_STARTED = 'not_started';
const IN_REVIEW = 'in_review';

const DeliverableTimeline = ({ deliverable, isForBrand, subcampaignState }: Props) => {
  const { contentApproval, contentApprovalTimeline, deliverableType } = deliverable;
  const availableTaskTypes = [NEXT_TASK, UPCOMING_TASK];
  const isTaskTypeOther = deliverableType === 'other';
  const isEntertainmentVideoTask = isEntertainmentVideo(deliverableType);

  const getIsOneAssetAndCaptionApprovedStatus = (): boolean => {
    const isOneAssetApproved = (contentApproval?.assets || []).some(a => a.approvalStatus === APPROVED);
    const isCaptionApproved = contentApproval?.caption.approvalStatus === APPROVED;

    return isOneAssetApproved && isCaptionApproved;
  };

  const getTaskType = (status?: string) => {
    if (status === IN_PROGRESS || status === IN_REVIEW) {
      return CURRENT_TASK;
    }

    if (status === NOT_STARTED) {
      return availableTaskTypes.shift() || TASK;
    }

    return TASK;
  };

  const getZalandoOutfitTimelines = (): Array<DeliverableTimelineType> => ([{
    ...contentApprovalTimeline,
    taskType: getTaskType(contentApprovalTimeline.status),
    timelineType: CONTENT_APPROVAL_TIMELINE
  }]);

  const getTimelines = (): Array<DeliverableTimelineType> => {
    const timelines: Array<DeliverableTimelineType> = [];
    const commonTimelines = [CONTENT_GO_LIVE_TIMELINE];

    if (!isTaskTypeOther && !isEntertainmentVideoTask) {
      commonTimelines.push(INSIGHTS_PROVISION_TIMELINE);
    }

    if (deliverable.requiresApproval) {
      timelines.push({
        ...contentApprovalTimeline,
        taskType: getTaskType(contentApprovalTimeline.status),
        timelineType: CONTENT_APPROVAL_TIMELINE
      });
    }

    commonTimelines.forEach(timelineType => {
      timelines.push({
        ...deliverable[timelineType],
        taskType: getTaskType(deliverable[timelineType].status),
        timelineType
      });
    });

    return timelines;
  };

  const renderTimeline = (timeline: DeliverableTimelineType, isFirst: boolean): React.ReactNode => {
    const { startDate, endDate, taskType, timelineType, status } = timeline;
    const statusToRender = subcampaignState !== 'accepted' ? '' : status;
    const isTaskTypeOtherWithContentApproval = isTaskTypeOther && deliverable.requiresApproval;

    const classes = classnames('deliverable-timeline__card', {
      'deliverable-timeline__card--selected': taskType === CURRENT_TASK,
      'deliverable-timeline__card--hidden': isTaskTypeOtherWithContentApproval && isFirst
    });

    const statusType = getDeliverableTimelineStatusType(timeline, isForBrand);
    const hideBrandReviewStatus = !isForBrand && getIsOneAssetAndCaptionApprovedStatus();

    const statusClasses = classnames('deliverable-timeline__card-item',
      `deliverable-timeline__card-item--${statusType}`);

    let label = isTaskTypeOther
      ? taskType
      : `${taskType}: ${__t(`deliverable_timeline.${snakeCase(timelineType)}`)}`;

    if (isTaskTypeOther && label === NEXT_TASK) {
      label = __t('deliverable_timeline.other_task_upcoming_timeline');
    }

    return (
      <div className={classes} key={timeline.timelineType}>
        <div className="deliverable-timeline__card-item">
          {getDisplayDateRange(startDate, endDate)}
        </div>
        <div className="deliverable-timeline__card-item">
          {label}
        </div>
        {
          !hideBrandReviewStatus && (
            <div className={statusClasses}>
              {__t(`deliverable_timeline.${statusToRender}.${isForBrand ? 'brand' : 'influencer'}`)}
            </div>
          )
        }
        {
          (!isFirst && !isTaskTypeOtherWithContentApproval) && (
            <div className="deliverable-timeline__task-transition">
              <SVGDynamicIcon
                name="arrow-right-custom-bg"
                width={24}
                height={24}
                customColor="grey-light"
              />
            </div>
          )
        }
      </div>
    );
  };

  const timelines = deliverableType === DELIVERABLE_TYPE_ZALANDO_OUTFIT
    ? getZalandoOutfitTimelines()
    : getTimelines();

  return (
    <div className="deliverable-timeline">
      {
        timelines.map((timeline, index) => renderTimeline(timeline, index === 0))
      }
    </div>
  );
};

export default DeliverableTimeline;
